import { ApiService } from './ApiService';
import { HealthSystemAPIControllersSensorDevicesResponsesSensorDeviceResponse } from '../api/HealthSystemApi';
import {
  DiscoverSensorDevicesResult,
  SENSOR_PROVIDER_TYPE,
  SensorDevice,
  SensorDeviceLocationChangeReason,
  SensorDeviceProvider,
  SensorDeviceType,
  SensorProviderStatus,
  UNCONFIGURED_SENSOR_PROVIDER
} from '../models/sensor-devices';

/**
 * The farm's sensor devices and the pen each is bound to - the four endpoints behind the Sensor devices tab.
 *
 * All four are in the generated client, so this is a wrapper rather than a hand-written service. Two things it
 * earns:
 *
 * 1. **Nullable normalisation.** The generated response declares every optional field as `?: T | null`. Every
 *    consumer would otherwise have to treat "absent" and "null" as possibly different, when the API only ever
 *    means "not bound" / "never seen" by either.
 * 2. **`format: 'json'` on the two 204 endpoints.** Assign and unassign are generated without a response
 *    format, and the generated client only parses a body when one is asked for - so a 409 naming the pen's
 *    occupying device, or a 400 refusing a weather station, would reach the caller as a bare `Response` with
 *    nothing readable on it. Asking for JSON gets the error body parsed where `extractApiErrorMessage` and
 *    `extractApiErrorCode` can find it. On the 204 itself the parse fails harmlessly: the client only assigns
 *    the parse error when the response was *not* ok, so a success still resolves.
 */
export class SensorDevicesService extends ApiService {
  getDevices = async (farmId: string): Promise<SensorDevice[]> => {
    const result = await this.api.sensorDevices.sensorDevicesGetAll({ farmId });

    return (result.data ?? []).map(toSensorDevice);
  };

  /**
   * Whether this farm's sensor provider is configured, switched on, and has the organization name it cannot
   * work without.
   *
   * Reads the same `/configuration-providers` list the External Providers panel edits, and narrows it to the
   * one provider this screen depends on - so the screen can say "there is no provider" up front instead of
   * showing an empty table and letting the reader discover it by pressing Refresh and collecting a 3005.
   */
  getProviderStatus = async (farmId: string): Promise<SensorProviderStatus> => {
    const result = await this.api.farmConfigurationProviders.farmConfigurationProvidersGetAll({ farmId });
    const provider = (result.data ?? []).find((candidate) => candidate.providerType === SENSOR_PROVIDER_TYPE);

    if (!provider) {
      return UNCONFIGURED_SENSOR_PROVIDER;
    }

    // `settings` is `any` on the wire - a free-form bag per provider type - so the one field this screen
    // needs is read defensively rather than trusted to be a string.
    const organizationName = provider.settings?.organizationName;

    return {
      isConfigured: true,
      isEnabled: provider.isEnabled,
      organizationName: typeof organizationName === 'string' && organizationName.trim() ? organizationName : null
    };
  };

  /**
   * Asks the provider what devices it has right now, instead of waiting for the daily sync worker.
   *
   * Registers and binds only - no readings are imported, so a device this turns up has empty climate charts
   * until the worker next runs.
   */
  discover = async (farmId: string): Promise<DiscoverSensorDevicesResult> => {
    const result = await this.api.sensorDevices.sensorDevicesDiscover({ farmId });

    return result.data;
  };

  /**
   * Binds the device to a pen.
   *
   * `reason` decides what happens to the readings the device has already sent: `Correction` moves them to the
   * new pen with it, `Relocation` leaves them on the pen they were measured in. Sent explicitly on every call
   * rather than relying on the API's own default, since the caller always knows which one it asked the farmer.
   */
  assignLocation = async (
    farmId: string,
    sensorDeviceId: string,
    locationId: string,
    reason: SensorDeviceLocationChangeReason
  ): Promise<void> => {
    await this.api.sensorDevices.sensorDevicesAssignLocation(
      { farmId, sensorDeviceId },
      { locationId, reason },
      { format: 'json' }
    );
  };

  /** Frees the pen. Readings already measured keep it; nothing re-binds the device automatically. */
  unassignLocation = async (farmId: string, sensorDeviceId: string): Promise<void> => {
    await this.api.sensorDevices.sensorDevicesUnassignLocation({ farmId, sensorDeviceId }, { format: 'json' });
  };
}

const toSensorDevice = (
  response: HealthSystemAPIControllersSensorDevicesResponsesSensorDeviceResponse
): SensorDevice => ({
  id: response.id,
  provider: response.provider as SensorDeviceProvider,
  externalId: response.externalId,
  externalName: response.externalName,
  externalDomain: response.externalDomain,
  deviceType: response.deviceType as SensorDeviceType,
  measuredTags: response.measuredTags ?? [],
  locationId: response.locationId ?? null,
  locationNumber: response.locationNumber ?? null,
  locationRoom: response.locationRoom ?? null,
  locationPen: response.locationPen ?? null,
  lastSeenAt: response.lastSeenAt ?? null,
  isRetired: response.isRetired,
  retiredAt: response.retiredAt ?? null
});

export default SensorDevicesService;
